import { close } from '@/features/sidebar';
import { useSupportChat } from '@/features/support';
import { useAnalytics } from '@/services/analytics';
import type { CopilotSession } from '@customerio/hydra-copilot';
import { useCopilotTool, useCopilotMessages } from '@customerio/hydra-copilot/react';
import { Button, useSnackbars } from '@customerio/pluma-components/react';
import { z } from '@customerio/hydra-validation';

const CHAT_LABEL = 'Chat with support';
const FORM_LABEL = 'Open support form';

const REQUEST_SCHEMA = z.toJSONSchema(
  z.object({
    body: z
      .string()
      .describe(
        'A comprehensive summary of what the user needs help with, written in first person. Include: 1) The specific problem or question they have, 2) What they\'ve already tried or checked, 3) Any error messages or symptoms, 4) Their goal or desired outcome. Synthesize the entire conversation context into a clear, actionable summary that gives support agents the full context without needing to read the chat history. Example: "I am trying to set up email campaigns but getting a 403 error when accessing the campaigns page. I have checked my permissions and tried clearing my browser cache, but the issue persists. I need to be able to create and send campaigns for my marketing team."',
      ),
  }),
);

type SupportButtonProps = { body: string; session: CopilotSession };

/**
 * The button label the model quotes has to be the one the user sees, so both
 * the button and the tool registration read it from here.
 */
function useSupportHandoff() {
  const support = useSupportChat();
  const canOpenChat = support.isHandoverAllowed === true && support.zendeskChatEnabled === true;

  return {
    ...support,
    canOpenChat,
    label: canOpenChat ? CHAT_LABEL : FORM_LABEL,
    // Until Zendesk access loads the label sits on the form fallback and can
    // still flip to chat, so the model gets no label to quote. Access that
    // never loads at all — a masquerading user, a failed or offline request —
    // keeps the model generic rather than risking the wrong label.
    isLabelKnown: support.isVisible,
  };
}

function SupportButton({ body, session }: SupportButtonProps) {
  const { openChat, openForm, canOpenChat, label } = useSupportHandoff();
  const { track } = useAnalytics();
  const snackbars = useSnackbars();
  const messages = useCopilotMessages(session);

  const messageCount = messages.length;
  const hadToolCalls = messages.some(
    (m) => m.type === 'tool_call' && m.tool_calls.some((tc) => tc.name !== 'show_support_form'),
  );

  return (
    <div>
      <Button
        onClick={async () => {
          const handoffProps = { 'Message Count': messageCount, 'Had Tool Calls': hadToolCalls };
          if (canOpenChat) {
            const success = await openChat({
              message: `[ASSISTANT TRANSFER ${session.id}] ${body}`,
              sessionId: session.id,
            });
            if (success) {
              close();
              track('aiAssistant', 'transferredToTsChat', handoffProps);
            } else {
              snackbars.create({
                message: 'Failed to open support chat. Try again.',
                variant: 'critical',
              });
            }
            return success;
          } else {
            openForm(`[ASSISTANT TRANSFER ${session.id}] ${body}`);
            track('aiAssistant', 'transferredToTsSupportForm', handoffProps);
            return true;
          }
        }}
      >
        {label}
      </Button>
    </div>
  );
}

export function ShowSupportFormTool() {
  const { label, isLabelKnown } = useSupportHandoff();
  const buttonName = isLabelKnown ? `the "${label}" button` : 'the support button';

  useCopilotTool({
    type: 'client',
    name: 'show_support_form',
    description: `
      Hands this conversation to Customer.io support. It puts a button in the chat that carries a summary you write to the support team.

      Calling this tool is the only way you can reach Customer.io support, and it is how you open, file, submit or escalate a support ticket for the user. You cannot send the request yourself: the button opens a support form or a support chat that the user has to send. So when the user asks you to submit or open a ticket, call this tool, then tell the user to click the button. Never reply that you are unable to do it, and never send the user to email or to another support channel instead.

      Call this tool when the user asks for a human, asks for a ticket, or asks you to escalate. Solve the problem yourself first if you can, and ask the user for the missing details if the request is too thin to summarise. This is not for general feedback or for feature requests, and do not offer a handoff the user has not asked for. Upgrade and plan requests go to show_premium_upgrade_form instead, even when the user asks for a person.

      The button label depends on the user's account, so the tool result tells you the label that is on screen. Use that exact label when you point the user at the button, and never invent a different one. A result that names no label gives you generic wording instead: keep it as it is, and do not fill in a label of your own.

      If the user cannot find the button, say where it is: in the chat, just above your message. Call this tool again only if the user says that there is no button at all. Each button starts its own support request, so tell the user to use one button only.

      <sample-chat>
      ...continuation of the chat...
      User: Talk to a human
      Model: Sure, I can connect you to support.
      Model: show_support_form({ body: "I need help configuring my account settings but I'm not sure where to find the options I need. My goal is to update my notification preferences and user permissions." })
      </sample-chat>

      <sample-chat>
      ...continuation of the chat...
      User: My campaigns aren't sending and I'm getting a 403 error
      Model: Let me help you troubleshoot this. Have you checked your sending permissions?
      User: Yes, I checked and they look correct. I also tried refreshing the page
      Model: Can you try clearing your browser cache?
      User: I did that too, still getting the same error. Can I talk to someone?
      Model: Let me connect you to support since we've tried the basic troubleshooting steps.
      Model: show_support_form({ body: "I am unable to send campaigns and receiving a 403 error when attempting to do so. I have already checked my sending permissions which appear correct, refreshed the page, and cleared my browser cache, but the error persists. I need to get my campaigns sending again for my marketing schedule." })
      </sample-chat>

      <sample-chat>
      ...continuation of the chat...
      User: Can you please submit the ticket for me?
      Model: show_support_form({ body: "My automations were queued over the weekend, but the report shows 7,556 messages sent on Monday across four automations. I expected nothing to send while the templates were queued. I need someone to check why these messages went out." })
      Model: I've written up the details and attached them. Click ${buttonName} just above this message, then send it to the support team from there.
      </sample-chat>
    `,
    requestSchema: REQUEST_SCHEMA,
    handler() {
      return `The support button is now in the chat, with your summary attached. You cannot send the request yourself, so tell the user to click ${buttonName}.`;
    },
    render({ args, session }) {
      return <SupportButton body={args.body} session={session} />;
    },
  });

  return null;
}
